import { OrgMoveDialogView, OrgTreeView, OrgUnitDetailPaneView, OrgUnitFormView } from '@today/ui-domain'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { OrganizationCapabilities } from '@/auth/organization-capabilities'
import type { OrgT } from '@/hooks/organization/org-editor'
import { ORG_ROW_HEIGHT } from '@/hooks/organization/org-window'
import { useOrgMove } from '@/hooks/organization/useOrgMove'
import { ORG_VIEWPORT_HEIGHT, useOrgTree } from '@/hooks/organization/useOrgTree'
import { useOrgTypeList } from '@/hooks/organization/useOrgTypes'
import { toDetailView, useOrgUnit } from '@/hooks/organization/useOrgUnit'
import type { useOrgUrlState } from '@/hooks/organization/useOrgUrlState'
import { ORGANIZATION_NAMESPACE } from '@/i18n'
import type { OrgTypeDTO } from '@/services/organization.service'
import { AssignmentsPanel, LocationsPanel } from './UnitPanels'

type Url = ReturnType<typeof useOrgUrlState>
type Tree = ReturnType<typeof useOrgTree>
type Detail = ReturnType<typeof useOrgUnit>
type Move = ReturnType<typeof useOrgMove>

interface UnitsTabProps {
	caps: OrganizationCapabilities
	slug: string
	url: Url
}

/** The search box text: local (keeps what is typed), mirrored trimmed into `?q=`. */
function useSearchText(url: Url) {
	const [text, setText] = useState(url.q)
	return {
		text,
		change: (value: string) => {
			setText(value)
			url.setQuery(value)
		},
	}
}

/** Present only when the viewer may write units. */
const whenWritable = <F,>(canWrite: boolean, fn: F): F | undefined => (canWrite ? fn : undefined)

function UnitsTree(p: {
	t: OrgT
	tree: Tree
	url: Url
	search: ReturnType<typeof useSearchText>
	detail: Detail
	move: Move
	canWrite: boolean
}) {
	const { tree } = p
	return (
		<OrgTreeView
			t={p.t}
			state={tree.state}
			rows={tree.rows}
			window={tree.window}
			rowHeight={ORG_ROW_HEIGHT}
			viewportHeight={ORG_VIEWPORT_HEIGHT}
			scrollTop={tree.scrollTop}
			selectedId={p.url.unitId}
			query={p.search.text}
			matchCount={tree.matchCount}
			onQueryChange={p.search.change}
			onScroll={tree.setScrollTop}
			onToggle={tree.toggle}
			onSelect={p.url.selectUnit}
			onRetry={tree.retry}
			onNew={whenWritable(p.canWrite, () => p.detail.openCreate(null))}
			onDrop={whenWritable(p.canWrite, p.move.drop)}
			isLegalDrop={p.move.isLegalDrop}
		/>
	)
}

function UnitDetail(p: {
	tree: Tree
	detail: Detail
	move: Move
	types: OrgTypeDTO[]
	caps: OrganizationCapabilities
	slug: string
}) {
	const { t, i18n } = useTranslation(ORGANIZATION_NAMESPACE)
	const unit = p.detail.unit
	const canWrite = p.caps.write.units && unit !== null
	const view = unit ? toDetailView(unit, p.types, p.tree.model.byId.get(unit.parentId ?? ''), i18n.language) : null
	return (
		<OrgUnitDetailPaneView
			t={t}
			state={p.detail.state}
			unit={view}
			onRetry={p.detail.retry}
			onEdit={whenWritable(canWrite, () => unit && p.detail.openEdit(unit))}
			onNewChild={whenWritable(canWrite, () => p.detail.openCreate(unit))}
			onMove={whenWritable(canWrite, () => unit && p.move.open(unit))}
		>
			{unit ? <LocationsPanel key={`l-${unit.id}`} unit={unit} canWrite={p.caps.write.units} /> : null}
			{unit && p.caps.panels.assignments ? (
				<AssignmentsPanel key={`a-${unit.id}`} unit={unit} caps={p.caps} slug={p.slug} />
			) : null}
		</OrgUnitDetailPaneView>
	)
}

function UnitDialogs({ t, detail, move, types }: { t: OrgT; detail: Detail; move: Move; types: OrgTypeDTO[] }) {
	const dialog = move.dialog
	return (
		<>
			<OrgUnitFormView
				t={t}
				editor={detail.editor}
				typeOptions={types.map((type) => ({ id: type.code, label: `${type.code} · ${type.name}` }))}
				parentLabel={detail.parentLabel}
				isBusy={detail.isSaving}
				onChange={detail.change}
				onSubmit={detail.submit}
				onClose={detail.close}
			/>
			<OrgMoveDialogView
				t={t}
				isOpen={dialog !== null}
				unitCode={dialog?.unit.code ?? ''}
				query={dialog?.query ?? ''}
				options={move.options}
				target={dialog?.target}
				error={dialog?.error ?? null}
				isBusy={move.isMoving}
				onQueryChange={move.setQuery}
				onTargetChange={move.setTarget}
				onConfirm={move.confirm}
				onClose={move.close}
			/>
		</>
	)
}

/** The Units tab: tree on the left, the selected unit's detail and panels on the right (REQ-002…REQ-008). */
export function UnitsTab({ caps, slug, url }: UnitsTabProps) {
	const { t } = useTranslation(ORGANIZATION_NAMESPACE)
	const search = useSearchText(url)
	const tree = useOrgTree({ enabled: caps.fetchAllowed, unitId: url.unitId, query: search.text })
	const types = useOrgTypeList(caps.tabs.types).data ?? []
	const detail = useOrgUnit({ unitId: url.unitId, enabled: caps.fetchAllowed, t, onSaved: url.selectUnit })
	const move = useOrgMove({ model: tree.model, t })
	return (
		<div className="flex flex-col gap-3">
			{move.dropError ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{move.dropError}
				</p>
			) : null}
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<UnitsTree
					t={t}
					tree={tree}
					url={url}
					search={search}
					detail={detail}
					move={move}
					canWrite={caps.write.units}
				/>
				<UnitDetail tree={tree} detail={detail} move={move} types={types} caps={caps} slug={slug} />
			</div>
			<UnitDialogs t={t} detail={detail} move={move} types={types} />
		</div>
	)
}
